/**
 * Admin System Logs Page
 * View and filter system logs from job execution
 */

import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { Link } from 'react-router-dom';
import { getApiUrl } from '../../config/api';
import {
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';

interface LogEntry {
  id: number;
  job_type: string;
  status: string;
  software_id: number | null;
  software_name: string | null;
  scheduled_at: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  result: string | null;
  error_message: string | null;
  triggered_by: string;
  log_level: 'error' | 'success' | 'info';
}

interface LogStats {
  error_rate: {
    total_jobs: number;
    failed_jobs: number;
    completed_jobs: number;
  };
  common_errors: Array<{
    error_message: string;
    count: number;
    last_occurrence: string;
  }>;
  activity_by_hour: Array<{
    hour: string;
    total: number;
    errors: number;
  }>;
}

export default function AdminLogs() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [stats, setStats] = useState<LogStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '50',
      });

      if (levelFilter) params.append('level', levelFilter);
      if (searchQuery) params.append('search', searchQuery);

      const [logsRes, statsRes] = await Promise.all([
        fetchWithAuth(getApiUrl(`api/v1/admin/logs?${params}`)),
        fetchWithAuth(getApiUrl('api/v1/admin/logs/stats')),
      ]);

      const logsData = await logsRes.json();
      const statsData = await statsRes.json();

      if (logsData.success) {
        setLogs(logsData.data);
        setTotalPages(logsData.pagination.total_pages);
      }
      if (statsData.success) {
        setStats(statsData.data);
      }
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentPage, levelFilter, searchQuery]);

  const getLogLevelIcon = (level: string) => {
    switch (level) {
      case 'error':
        return <XCircleIcon className="w-4 h-4" />;
      case 'success':
        return <CheckCircleIcon className="w-4 h-4" />;
      case 'info':
        return <InformationCircleIcon className="w-4 h-4" />;
      default:
        return <InformationCircleIcon className="w-4 h-4" />;
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level) {
      case 'error':
        return 'text-red-400 bg-red-900/20 border-red-500/30';
      case 'success':
        return 'text-green-400 bg-green-900/20 border-green-500/30';
      case 'info':
        return 'text-blue-400 bg-blue-900/20 border-blue-500/30';
      default:
        return 'text-gray-400 bg-gray-800/30 border-gray-700';
    }
  };

  const formatDuration = (ms: number | null) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading logs...</p>
        </div>
      </div>
    );
  }

  const errorRate = stats?.error_rate
    ? ((stats.error_rate.failed_jobs / stats.error_rate.total_jobs) * 100).toFixed(1)
    : '0';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-100">System Logs</h1>
          <p className="text-gray-400 mt-1">Monitor system events and job execution logs</p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-cyber-blue-500/10 text-cyber-blue-400 border border-cyber-blue-500/30 rounded hover:bg-cyber-blue-500/20 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
            <div className="text-sm text-gray-400 mb-1">Total Events (24h)</div>
            <div className="text-2xl font-bold text-gray-100">
              {stats.error_rate.total_jobs}
            </div>
          </div>
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
            <div className="text-sm text-green-400 mb-1">Success Rate</div>
            <div className="text-2xl font-bold text-green-400">
              {(100 - parseFloat(errorRate)).toFixed(1)}%
            </div>
          </div>
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4">
            <div className="text-sm text-red-400 mb-1">Error Rate</div>
            <div className="text-2xl font-bold text-red-400">{errorRate}%</div>
          </div>
        </div>
      )}

      {/* Common Errors */}
      {stats && stats.common_errors.length > 0 && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
            <h3 className="text-lg font-semibold text-red-400">Most Common Errors (7 days)</h3>
          </div>
          <div className="space-y-2">
            {stats.common_errors.map((error, idx) => (
              <div
                key={idx}
                className="bg-red-900/10 border border-red-500/20 rounded p-3 flex items-start justify-between"
              >
                <div className="flex-1">
                  <p className="text-sm text-red-200 font-mono">{error.error_message}</p>
                  <p className="text-xs text-red-400 mt-1">
                    Last occurred: {new Date(error.last_occurrence).toLocaleString()}
                  </p>
                </div>
                <span className="ml-4 px-2 py-1 bg-red-500/20 text-red-300 rounded text-xs font-semibold">
                  {error.count}x
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-gray-400" />
            <select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              className="px-3 py-2 bg-gray-900/50 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-cyber-blue-500"
            >
              <option value="">All Levels</option>
              <option value="error">Errors</option>
              <option value="success">Success</option>
              <option value="info">Info</option>
            </select>
          </div>

          <div className="flex items-center gap-2 px-3 py-2 bg-gray-900/50 border border-gray-700 rounded flex-1 max-w-md">
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none text-sm text-gray-100 placeholder-gray-500 flex-1"
            />
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-100 mb-4">Event Log</h3>
        <div className="space-y-2">
          {logs.map((log) => (
            <div
              key={log.id}
              className={`border rounded-lg p-3 ${getLogLevelColor(log.log_level)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <div className="mt-0.5">{getLogLevelIcon(log.log_level)}</div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Link
                        to={`/admin/jobs/${log.id}`}
                        className="text-sm font-medium hover:underline"
                      >
                        Job #{log.id}
                      </Link>
                      <span className="text-xs px-2 py-0.5 bg-gray-900/30 border border-gray-700/50 rounded">
                        {log.job_type}
                      </span>
                      {log.software_name && (
                        <span className="text-xs text-gray-400">{log.software_name}</span>
                      )}
                    </div>

                    {log.error_message && (
                      <p className="text-sm font-mono mt-2 bg-gray-900/30 border border-gray-700/50 rounded p-2">
                        {log.error_message}
                      </p>
                    )}

                    {log.result && !log.error_message && (
                      <p className="text-xs text-gray-400 mt-1">{log.result}</p>
                    )}

                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>Duration: {formatDuration(log.duration_ms)}</span>
                      <span>Triggered by: {log.triggered_by}</span>
                      {log.completed_at && (
                        <span>{new Date(log.completed_at).toLocaleString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
          <span className="text-sm text-gray-400">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-700 text-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-700 text-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
